// frontend/src/api.js 

import axios from 'axios';
import log from './logger'; // Import the logger
// import { logout } from './contexts/AuthContext'; // Uncomment if using logout

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';


const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Send cookies with requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to set headers
export const setAuthHeaders = (token, tenantId) => {
  //console.log('api setAuthHeaders: login called with token:', token);

  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }

  if (tenantId) {
    api.defaults.headers.common['X-Tenant-ID'] = tenantId;
  } else {
    delete api.defaults.headers.common['X-Tenant-ID'];
  }
};

// Add a request interceptor to include the JWT
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwtToken');
    const tenantId = localStorage.getItem('tenantId');

    // Only add Authorization header if the request is not to the auth endpoints
    if (token && !config.url.includes('/auth')) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add X-Tenant-ID header if tenantId exists and the request is not to auth endpoints
    if (tenantId && !config.url.includes('/auth')) {
      config.headers['X-Tenant-ID'] = tenantId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Flag to indicate if refresh token request is in progress
let isRefreshing = false;
// Array to hold pending requests while token is being refreshed
let failedQueue = [];

// Function to process the failed requests queue
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Add a response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;

    // If error response is 401 and the request has not been retried yet
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If a refresh token request is already in progress, queue the request
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        // logout(); // Uncomment if using logout
        return Promise.reject(error);
      }

      return new Promise(function (resolve, reject) {
        api
          .post('/auth/refresh', { refreshToken })
          .then(({ data }) => {
            const { accessToken } = data;
            localStorage.setItem('jwtToken', accessToken);
            setAuthHeaders(accessToken, localStorage.getItem('tenantId'));
            originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;
            processQueue(null, accessToken);
            resolve(api(originalRequest));
          })
          .catch((err) => {
            console.error('api: Token refresh failed:', err);
            processQueue(err, null);
            // logout(); // Uncomment if using logout
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(error);
  }
);

// **API Functions with Consistent Logging and Relative Paths**

export const getSectors = () => {
  const url = '/sectors';

  //log.debug(`GET Request to: ${url}`);

  return api.get(url)
    .then(response => {
      //log.debug('GET /sectors Response:', response.data);
      return response.data;
    })
    .catch(error => {
      log.error('GET /sectors Error:', error);
      throw error;
    });
};

export const getSectorTemplates = (sectorId) => {
  const url = `/sectors/${sectorId}/templates`;
  //log.debug(`GET Request to: ${url}`);
  return api.get(url)
    .then(response => {
      log.debug(`GET ${url} Response:`, response.data);
      return response.data;
    })
    .catch(error => {
      log.error(`GET ${url} Error:`, error);
      throw error;
    });
};

export const generateTemplate = (sectorId) => {
  const url = `/sectors/${sectorId}/templates/generate`;
  //log.debug(`POST Request to: ${url}`);
  return api.post(url)
    .then(response => {
      log.debug(`POST ${url} Response:`, response.data);
      return response.data;
    })
    .catch(error => {
      log.error(`POST ${url} Error:`, error);
      throw error;
    });
};

export const updateTemplate = (templateId, template) => { // **UPDATED: Accept 'template' string instead of 'questions' array**
  const url = `/faqs/${templateId}`;
  //log.debug(`PUT Request to: ${url} with data:`, { template });
  return api.put(url, { template })
    .then(response => {
      log.debug(`PUT ${url} Response:`, response.data);
      return response.data;
    })
    .catch(error => {
      log.error(`PUT ${url} Error:`, error);
      throw error;
    });
};

export const deleteTemplate = (templateId) => {
  const url = `/faqs/${templateId}`;
  log.debug(`DELETE Request to: ${url}`);
  return api.delete(url)
    .then(response => {
      log.debug(`DELETE ${url} Response:`, response.data);
      return response.data;
    })
    .catch(error => {
      log.error(`DELETE ${url} Error:`, error);
      throw error;
    });
};

// **New Function for Fetching Call Logs**
export const getCallLogs = () => {
  const url = '/call-logs';
  log.debug(`GET Request to: ${url}`);
  return api.get(url)
    .then(response => {
      log.debug('GET /call-logs Response:', response.data);
      return response.data;
    })
    .catch(error => {
      log.error('GET /call-logs Error:', error);
      throw error;
    });
};

export const getKnowledgeBaseDetails = () => {
  const url = '/knowledge-base';
  //log.debug(`GET Request to: ${url}`);
  return api.get(url)
    .then(response => {
      //log.debug('GET /knowledge-base Response:', response.data);
      return response.data;
    })
    .catch(error => {
      log.error('GET /knowledge-base Error:', error);
      throw error;
    });
};

export const createKnowledgeBase = (name, text, businessProfileId) => {
  const url = '/knowledge-base';
  log.debug(`POST Request to: ${url} with data:`, { name, text, businessProfileId });
  return api.post(url, { name, text, businessProfileId })
    .then(response => {
      log.debug('POST /knowledge-base Response:', response.data);
      return response.data;
    })
    .catch(error => {
      log.error('POST /knowledge-base Error:', error);
      throw error;
    });
};

export const updateKnowledgeBase = (id, name, text) => {
  const url = `/knowledge-base/${id}`;
  log.debug(`PUT Request to: ${url} with data:`, { name, text });
  return api.put(url, { name, text })
    .then(response => {
      log.debug(`PUT ${url} Response:`, response.data);
      return response.data;
    })
    .catch(error => {
      log.error(`PUT ${url} Error:`, error);
      throw error;
    });
};

export const deleteKnowledgeBase = (id) => {
  const url = `/knowledge-base/${id}`;
  log.debug(`DELETE Request to: ${url}`);
  return api.delete(url)
    .then(response => {
      log.debug(`DELETE ${url} Response:`, response.data);
      return response.data;
    })
    .catch(error => {
      log.error(`DELETE ${url} Error:`, error);
      throw error;
    });
};

export const activateKnowledgeBase = (id, active) => {
  const url = `/knowledge-base/${id}/activate`;
  log.debug(`PUT Request to: ${url} with data:`, { active });
  return api.put(url, { active })
    .then(response => {
      log.debug(`PUT ${url} Response:`, response.data);
      return response.data;
    })
    .catch(error => {
      log.error(`PUT ${url} Error:`, error);
      throw error;
    });
};

// **New Function for Activating Prompts**
export const activatePrompt = (id, active) => {
  const url = `/prompt-templates/${id}/activate`;
  log.debug(`PUT Request to: ${url} with data:`, { active });
  return api.put(url, { active })
    .then(response => {
      log.debug(`PUT ${url} Response:`, response.data);
      return response.data;
    })
    .catch(error => {
      log.error(`PUT ${url} Error:`, error);
      throw error;
    });
};

// Add this to your api.js
export const getBusinessProfile = () => {
  const url = '/business-profile';
  //log.debug(`getBusinessProfile GET Request to: ${url}`);
  return api.get(url)
    .then(response => {
      //log.debug('GET /business-profile Response:', response.data);
      return response.data;
    })
    .catch(error => {
      log.error('GET /business-profile Error:', error);
      throw error;
    });
};

export const getActiveKnowledgeBase = () => {
  const url = '/knowledge-base/active';
  //log.debug(`GET Request to: ${url}`);
  return api.get(url)
    .then(response => {
      //log.debug('GET /knowledge-base/active Response:', response.data);
      return response.data;
    })
    .catch(error => {
      log.error('GET /knowledge-base/active Error:', error);
      throw error;
    });
};

export const getActivePromptTemplate = () => {
  const url = '/prompt-templates/active';
 // log.debug(`GET Request to: ${url}`);
  return api.get(url)
    .then(response => {
      //log.debug('GET /prompt-templates/active Response:', response.data);
      return response.data;
    })
    .catch(error => {
      log.error('GET /prompt-templates/active Error:', error);
      throw error;
    });
};

export const bookAppointment = (appointmentDetails) => {
  const url = '/book-appointment';
  log.debug(`POST Request to: ${url} with data:`, appointmentDetails);
  return api.post(url, appointmentDetails)
    .then(response => {
      log.debug('POST /book-appointment Response:', response.data);
      return response.data;
    })
    .catch(error => {
      log.error('POST /book-appointment Error:', error);
      throw error;
    });
};

export const processTranscript = (callId, transcript, task) => {
  const url = '/process-transcript';
  log.debug(`POST Request to: ${url} with data:`, { callId, transcript, task });
  return api.post(url, { callId, transcript, task })
    .then(response => {
      log.debug(`POST /process-transcript (Task: ${task}) Response:`, response.data);
      return response.data;
    })
    .catch(error => {
      log.error(`POST /process-transcript (Task: ${task}) Error:`, error);
      throw error;
    });
};


export const getSystemConfiguration = () => {
  const url = '/system-configuration';
  //log.debug(`GET Request to: ${url}`);
  return api.get(url)
    .then(response => {
      //log.debug('GET /system-configuration:', response.data);
      return response.data;
    })
    .catch(error => {
      log.error('GET /system-configuration Error:', error);
      throw error;
    });
};

export const getCustomGreeting = () => {
  const url = '/custom-greetings';
  //log.debug(`GET Request to: ${url}`);
  return api.get(url)
    .then(response => {
      //log.debug('GET /custom-greetings Response:', response.data);
      return response.data;
    })
    .catch(error => {
      log.error('GET /custom-greetings Error:', error);
      throw error;
    });
};

export const updateCustomGreeting = ({ greeting, language }) => {
  const url = '/custom-greetings';
  log.debug(`POST Request to: ${url} with data:`, { greeting, language });
  return api.post(url, { greeting, language }) // Send both greeting and language
    .then(response => {
      log.debug('POST /custom-greetings Response:', response.data);
      return response.data;
    })
    .catch(error => {
      log.error('POST /custom-greetings Error:', error);
      throw error;
    });
};

// Add this to your frontend/src/api.js
export const generateGreetingAudio = () => {
  const url = '/custom-greetings/generate-audio';
  log.debug(`POST Request to: ${url}`);
  return api.post(url)
    .then(response => {
      log.debug('POST /custom-greetings/generate-audio Response:', response.data);
      return response.data; // Contains audioUrl
    })
    .catch(error => {
      log.error('POST /custom-greetings/generate-audio Error:', error);
      throw error;
    });
};

// Add these functions to src/api.js

export const fetchUsageData = () => {
  const url = '/usage';
  log.debug(`GET Request to: ${url}`);
  return api.get(url)
    .then(response => {
      log.debug('GET /usage Response:', response.data);
      return response.data;
    })
    .catch(error => {
      log.error('GET /usage Error:', error);
      throw error;
    });
};

export const fetchBillingData = () => {
  const url = '/billing';
  log.debug(`GET Request to: ${url}`);
  return api.get(url)
    .then(response => {
      log.debug('GET /billing Response:', response.data);
      return response.data;
    })
    .catch(error => {
      log.error('GET /billing Error:', error);
      throw error;
    });
};


// Exporting api and setAuthHeaders remains the same
export default api;
